import React, { FC, useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { ApplicationErrorBoundary } from "../errors";
import { COOKIES_DOMAIN, ONBOARDING_SURVEY_ID } from "../../constants";
import { useQueryParamAndCookie } from "../../hooks/useQueryParamAndCookie";
import { ROUTES } from "../../routes";
import { selectToken } from "../../store/reducers/auth";
import { selectCurrentUser } from "../../store/reducers/user";
import { useChangeMarketingNotificationMutation } from "../../store/services/user";

export const ActivationOutlet: FC = () => {
  const token = useSelector(selectToken);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);

  useQueryParamAndCookie("affid", undefined, {
    domain: COOKIES_DOMAIN,
  });

  const [changeMarketingNotification] =
    useChangeMarketingNotificationMutation();

  // turn off survey for now, as KYC modal is auto-opened on home page.
  const surveyEnabled = false; // toggle as desired
  const plainContent = true; // toggle as desired - mutually exclusive with surveyEnabled
  const surveyRedirect =
    surveyEnabled || plainContent ? ROUTES.AUTH_PHONE_CONFIRMED : ROUTES.HOME;

  useEffect(() => {
    if (token && user && user.phone) {
      if (ONBOARDING_SURVEY_ID && !user.marketing_data?.survey_displayed) {
        // always update survey_displayed to true, even though Survey is disabled,
        // in case we re-enable the Survey, we don't want existing users to be subject to it
        (async () => {
          await changeMarketingNotification({
            ...user?.marketing_data,
            survey_displayed: true,
          });

          navigate(surveyRedirect, {
            replace: true,
            state: { ...location.state },
          });
        })();
      } else {
        navigate(ROUTES.HOME, {
          replace: true,
          state: { ...location.state },
        });
      }
    }
  }, [
    changeMarketingNotification,
    location.state,
    navigate,
    token,
    user,
    surveyRedirect,
  ]);

  if (!token) {
    return <Navigate to={ROUTES.AUTH_LOGIN} state={{ ...location.state }} />;
  }

  return (
    <ApplicationErrorBoundary>
      <Outlet />
    </ApplicationErrorBoundary>
  );
};
